package com.yendo.mx.ui.home

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.LocationOff
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.material.icons.filled.DirectionsBike
import androidx.compose.material.icons.filled.Storefront
import androidx.compose.material.icons.filled.TwoWheeler
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.Producto
import com.yendo.mx.models.SeccionHome
import com.yendo.mx.models.DireccionGuardada
import com.yendo.mx.repository.RestauranteRepository
import com.yendo.mx.repository.HomeRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.ui.components.BuscadorYendo
import com.yendo.mx.ui.components.RestauranteRatingBadge
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.EstadoCarga
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.launch
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import com.yendo.mx.utils.validarRestauranteAbierto
import com.yendo.mx.utils.getAhora

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import coil3.size.Size
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter
import com.yendo.mx.utils.BackHandler

import com.yendo.mx.utils.esAndroid

// 🚀 FUNCIÓN COMPATIBLE MULTIPLATAFORMA (ANDROID, IOS, WEB) PARA LIMPIEZA DE TEXTO (0ms CPU, 0% CRASH EN JS)
private fun String.limpiarTextoCategoria(): String {
    return this.filter { it.isLetterOrDigit() || it.isWhitespace() }.trim()
}
internal val cacheChipsVista = mutableMapOf<String, List<ChipFiltro>>()

data class ChipFiltro(val nombre: String, val emoji: String)

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun VistaComida(
    listState: LazyListState = rememberSaveable(saver = LazyListState.Saver) { LazyListState() },
    direccionActiva: DireccionGuardada? = null,
    onAbrirUbicacion: () -> Unit = {},
    onRestauranteClick: (RestauranteFirebase) -> Unit,
    onProductoDirectoClick: (Producto, RestauranteFirebase) -> Unit = { _, _ -> },
    onVerTodoSeccion: (SeccionHome) -> Unit = {},
    onAbrirBuscador: () -> Unit = {},
    onBack: () -> Unit = {}
) {
    BackHandler(enabled = true) { onBack() }
    val scope = rememberCoroutineScope()
    val estadoConfig by ConfigManager.estadoConfig.collectAsState()

    var categoriaSeleccionada by rememberSaveable { mutableStateOf("Todos") }
    var textoBusqueda by remember { mutableStateOf(TextFieldValue("")) }
    var paginaActual by rememberSaveable { mutableIntStateOf(1) }

    // 🚀 REINICIAR A PÁGINA 1 AL CAMBIAR DE BÚSQUEDA, CATEGORÍA O ZONA
    LaunchedEffect(textoBusqueda.text, categoriaSeleccionada, estadoConfig) {
        paginaActual = 1
    }

    val idZonaActual = (estadoConfig as? EstadoCarga.Exito)?.id_zona

    // 🚀 INTEGRACIÓN CERO-LATENCIA CON BACKEND BFF EN CLOUD FUNCTIONS (Candado Anti-Refresco Repetido):
    val initialCatalog = remember(idZonaActual) {
        if (idZonaActual != null) HomeRepository.obtenerCacheCatalogo(idZonaActual, "COMIDA")
        else com.yendo.mx.repository.RespuestaCatalogoCloud()
    }
    val catalogoCloudState by produceState<com.yendo.mx.repository.RespuestaCatalogoCloud>(initialValue = initialCatalog, key1 = idZonaActual) {
        if (idZonaActual != null && value.negocios.isEmpty()) {
            value = HomeRepository.obtenerCatalogoCompletoCloud(idZonaActual, vistaTarget = "COMIDA")
        }
    }

    // 🚀 DESCONEXIÓN DE LISTENER PESADO EN COLD START:
    // Evita descargar cientos de documentos crudos por WebSocket si la Nube responde en 0ms
    val restaurantesBase by if (catalogoCloudState.negocios.isEmpty()) {
        RestauranteRepository.obtenerRestaurantes().collectAsState(initial = emptyList())
    } else {
        remember { mutableStateOf(emptyList<RestauranteFirebase>()) }
    }

    val seccionesComidaBase by if (catalogoCloudState.secciones.isEmpty()) {
        HomeRepository.observarSeccionesPorVista(idZonaActual ?: "", "COMIDA").collectAsState(initial = emptyList())
    } else {
        remember { mutableStateOf(emptyList<SeccionHome>()) }
    }

    val seccionesComida = if (catalogoCloudState.secciones.isNotEmpty()) catalogoCloudState.secciones else seccionesComidaBase
    val restaurantes = if (catalogoCloudState.negocios.isNotEmpty()) catalogoCloudState.negocios else restaurantesBase

    // 🚀 PRECARGA SOMBRA DE IMÁGENES EN SEGUNDO PLANO (Solo Android):
    val platformContext = LocalPlatformContext.current
    if (esAndroid()) {
        LaunchedEffect(restaurantes) {
            if (restaurantes.isNotEmpty()) {
                withContext(Dispatchers.Default) {
                    try {
                        val imageLoader = coil3.SingletonImageLoader.get(platformContext)
                        restaurantes.take(12).forEach { rest ->
                            val url = rest.infoPublica.logotipo.ifEmpty { rest.infoPublica.header }
                            if (url.isNotEmpty()) {
                                val req = ImageRequest.Builder(platformContext)
                                    .data(url)
                                    .size(350, 350)
                                    .memoryCachePolicy(coil3.request.CachePolicy.ENABLED)
                                    .diskCachePolicy(if (esAndroid()) coil3.request.CachePolicy.ENABLED else coil3.request.CachePolicy.DISABLED)
                                    .build()
                                imageLoader.enqueue(req)
                            }
                        }
                    } catch (_: Exception) { }
                }
            }
        }
    }

    val isDark = isSystemInDarkTheme()
    val colorFondoPegajoso = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    // 🚀 ESTIMACIÓN ÚNICA A NIVEL PANTALLA DE LOGÍSTICA DE ZONA (0ms en scroll de filas)
    val configZonaVal = remember { ConfigManager.configActiva }
    val deliveryZonaOkGlobal = remember(configZonaVal) {
        (configZonaVal?.logistica?.delivery_activo ?: true) && com.yendo.mx.utils.LogisticaUtils.estaPlataformaAbierta(configZonaVal)
    }

    // =================================================================
    // 🚀 CHIPS DINÁMICOS ASÍNCRONOS CON CACHÉ DE NAVEGACIÓN (0ms de parpadeo)
    // =================================================================
    // 🚀 CHIPS DINÁMICOS ASÍNCRONOS CON CACHÉ SÍNCRONA DE NAVEGACIÓN (0ms de parpadeo)
    // =================================================================
    val initialChips = remember(idZonaActual) { cacheChipsVista["COMIDA_$idZonaActual"] ?: listOf(ChipFiltro("Todos", "😋")) }
    val chipsDinamicos by produceState<List<ChipFiltro>>(
        initialValue = initialChips,
        estadoConfig,
        restaurantes
    ) {
        value = withContext(Dispatchers.Default) {
            val configZona = ConfigManager.configActiva
            val listaChips = mutableListOf<ChipFiltro>()
            listaChips.add(ChipFiltro("Todos", "😋"))

            if (idZonaActual != null) {
                val categoriasVivas = mutableSetOf<String>()
                val subcategoriasVivas = mutableSetOf<String>()

                restaurantes.filter {
                    it.ubicacion.idZona == idZonaActual &&
                            it.infoPublica.tipoNegocio == "RESTAURANTE" &&
                            it.operacion.visible &&
                            it.operacion.activoComercio &&
                            !it.operacion.bloqueadoPorAdmin &&
                            !(it.operacion.pausadoManualmente && it.operacion.pausaHasta > getAhora()) // 🚀 JERARQUÍA DE PAUSA
                }.forEach { rest ->
                    val catLimpia = rest.infoPublica.categoria.limpiarTextoCategoria()
                    if (catLimpia.isNotBlank()) categoriasVivas.add(catLimpia)

                    rest.infoPublica.subcategorias.forEach { sub ->
                        val subLimpia = sub.limpiarTextoCategoria()
                        if (subLimpia.isNotBlank()) subcategoriasVivas.add(subLimpia)
                    }
                }

                val catalogoMaestro = configZona?.clasificacion?.restaurantes ?: emptyList()

                // 🚀 ORDENACIÓN BASADA EN CATÁLOGO MAESTRO
                catalogoMaestro.filter { it.activa }.forEach { catMaestra ->
                    if (categoriasVivas.any { it.equals(catMaestra.nombre, ignoreCase = true) }) {
                        listaChips.add(ChipFiltro(nombre = catMaestra.nombre, emoji = catMaestra.emoji))
                    }
                }

                categoriasVivas.forEach { nombreCat ->
                    if (listaChips.none { it.nombre.equals(nombreCat, ignoreCase = true) }) {
                        listaChips.add(ChipFiltro(nombre = nombreCat, emoji = ""))
                    }
                }

                cacheChipsVista["COMIDA_$idZonaActual"] = listaChips
            }
            listaChips
        }
    }

    // =================================================================
    // 🚀 FILAS DE RESTAURANTES CON REMEMBER SÍNCRONO (0ms latencia para cero-clamping de scroll)
    // =================================================================
    val filas = remember(restaurantes, textoBusqueda.text, categoriaSeleccionada, idZonaActual, paginaActual) {
        if (idZonaActual == null) {
            emptyList()
        } else {
            val tiempoActualMs = getAhora()

            val filtrados = restaurantes.filter { rest ->
                if (!rest.operacion.visible || !rest.operacion.activoComercio || rest.operacion.bloqueadoPorAdmin) return@filter false
                if (rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > tiempoActualMs) return@filter false // 🚀 OCULTAR SI ESTÁ PAUSADO
                if (rest.infoPublica.tipoNegocio.isNotBlank() && rest.infoPublica.tipoNegocio != "RESTAURANTE") return@filter false

                val idZonaNegocio = rest.ubicacion.idZona.ifBlank { idZonaActual }
                val coincideZona = idZonaNegocio == idZonaActual
                val nombre = rest.infoPublica.nombre

                val catLimpia = rest.infoPublica.categoria.limpiarTextoCategoria()

                val coincideTexto = textoBusqueda.text.isBlank() ||
                        nombre.contains(textoBusqueda.text, ignoreCase = true) ||
                        catLimpia.contains(textoBusqueda.text, ignoreCase = true) ||
                        rest.infoPublica.subcategorias.any { it.contains(textoBusqueda.text, ignoreCase = true) }

                val coincideCategoria = categoriaSeleccionada == "Todos" ||
                        catLimpia.equals(categoriaSeleccionada, ignoreCase = true) ||
                        rest.infoPublica.subcategorias.any {
                            it.limpiarTextoCategoria().equals(categoriaSeleccionada, ignoreCase = true)
                        }

                coincideZona && coincideTexto && coincideCategoria
            }.sortedBy { rest ->
                val pausaHasta = rest.operacion.pausaHasta
                val estaEnPanico = rest.operacion.pausadoManualmente && pausaHasta > tiempoActualMs
                val estaAbiertoNormal = validarRestauranteAbierto(rest)

                val operacionActiva = estaAbiertoNormal && !estaEnPanico

                if (operacionActiva) 0 else 1
            }

            // 🚀 PAGINACIÓN PROGRESIVA: 12 locales (6 filas) por página
            val limiteAMostrar = (paginaActual * 12).coerceAtMost(filtrados.size)
            filtrados.take(limiteAMostrar).chunked(2).map { par ->
                val idKey = if (par.size == 2) "${par[0].id}_${par[1].id}" else par[0].id
                FilaRestaurantesState(idKey = idKey, restaurantes = par)
            }
        }
    }

    LaunchedEffect(chipsDinamicos) {
        if (chipsDinamicos.none { it.nombre == categoriaSeleccionada }) {
            categoriaSeleccionada = "Todos"
        }
    }

    // 🚀 DETECTOR DE SCROLL INFINITO SEGURO (derivedStateOf con aduana estricta)
    val cercaDelFinal by remember(listState) {
        derivedStateOf {
            val total = listState.layoutInfo.totalItemsCount
            val visibleItems = listState.layoutInfo.visibleItemsInfo
            val lastIndex = visibleItems.lastOrNull()?.index ?: -1
            total >= 6 && lastIndex >= total - 2
        }
    }

    LaunchedEffect(cercaDelFinal) {
        if (cercaDelFinal && filas.isNotEmpty()) {
            println("YENDO_PERF_LOG COMIDA: Incrementando paginaActual de $paginaActual a ${paginaActual + 1}")
            paginaActual++
        }
    }

    LazyColumn(
        state = listState,
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(top = 0.dp, bottom = 140.dp)
    ) {
        // 🚀 CABECERA DE BÚSQUEDA Y UBICACIÓN (item estándar para 0ms re-medición en scroll)
        item(key = "buscador_cabecera") {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(color = colorFondoPegajoso, shape = RoundedCornerShape(bottomStart = 24.dp, bottomEnd = 24.dp))
                    .padding(top = 4.dp, bottom = 12.dp)
            ) {
                // 🚀 BARRA DE UBICACIÓN COMPACTA
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onAbrirUbicacion() }
                        .padding(horizontal = 20.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.Default.LocationOn,
                        contentDescription = null,
                        tint = RosaYendo,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(Modifier.width(6.dp))

                    val tituloUbicacion = direccionActiva?.titulo?.ifBlank { direccionActiva.tipoUbicacion } ?: "Ubicación"
                    val calleSolo = direccionActiva?.direccion?.substringBefore(",")?.trim() ?: ""
                    val textoMostrar = if (calleSolo.isNotBlank()) "$tituloUbicacion • $calleSolo" else tituloUbicacion

                    Text(
                        text = textoMostrar,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = colorTextoPrincipal,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    Spacer(Modifier.width(4.dp))
                    Icon(
                        imageVector = Icons.Default.KeyboardArrowDown,
                        contentDescription = "Cambiar ubicación",
                        tint = colorTextoPrincipal,
                        modifier = Modifier.size(16.dp)
                    )
                }

                Box(modifier = Modifier.fillMaxWidth().clickable { onAbrirBuscador() }) {
                    BuscadorYendo(
                        valor = textoBusqueda,
                        alCambiar = { 
                            if (it.text.isNotBlank()) onAbrirBuscador()
                            else textoBusqueda = it
                        },
                        focoAutomatico = false,
                        onBack = onBack
                    )
                }
            }
        }

        if (chipsDinamicos.size > 1) {
            item(key = "categorias") {
                CategoriasHorizontal(
                    listaChips = chipsDinamicos,
                    categoriaActual = categoriaSeleccionada,
                    onCategoriaSeleccionada = { nuevaCat ->
                        if (categoriaSeleccionada != nuevaCat) {
                            categoriaSeleccionada = nuevaCat
                            scope.launch {
                                listState.animateScrollToItem(0)
                            }
                        }
                    }
                )
                Spacer(modifier = Modifier.height(16.dp))
            }
        }

        // 🚀 BANNERS DINÁMICOS Y CARROUSELES DE VISTA COMIDA (Solo en la categoría "Todos")
        if (categoriaSeleccionada == "Todos" && seccionesComida.isNotEmpty()) {
            items(
                items = seccionesComida,
                key = { seccion -> "sec_${seccion.id}" },
                contentType = { seccion -> "seccion_${seccion.tipoBloque}" }
            ) { seccion ->
                if (seccion.activo) {
                    when (seccion.tipoBloque) {
                        "BANNERS" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersDinamicos(
                                    banners = seccion.listaBanners,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "BANNERS_CUADRADOS" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersCuadrados(
                                    seccion = seccion,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "BANNERS_CUADRADOS_FULL" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersCuadradosFull(
                                    seccion = seccion,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "CARRUSEL_CUADRADO" -> {
                            EnchufeCarruselCuadrado(
                                seccion = seccion,
                                idZonaActual = idZonaActual ?: "",
                                colorTexto = colorTextoPrincipal,
                                onVerTodo = onVerTodoSeccion,
                                onProductoClick = { producto ->
                                    scope.launch {
                                        try {
                                            val restDoc = Firebase.firestore.collection("Negocios").document(producto.idRestaurante).get()
                                            if (restDoc.exists) {
                                                val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                val ahora = getAhora()
                                                val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                                                val esVisible = rest.operacion.visible && rest.operacion.activoComercio && !rest.operacion.bloqueadoPorAdmin && !estaPausado

                                                if (esVisible) {
                                                    val estaAbierto = rest.operacion.estado == "ACTIVO" && !estaPausado
                                                    val permiteProgramar = rest.operacion.programadoActivo
                                                    if (estaAbierto || permiteProgramar) {
                                                        onProductoDirectoClick(producto, rest)
                                                    } else {
                                                        onRestauranteClick(rest)
                                                    }
                                                }
                                            }
                                        } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                    }
                                }
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                        }
                        "CARRUSEL_LARGO" -> {
                            EnchufeCarruselLargo(
                                seccion = seccion,
                                idZonaActual = idZonaActual ?: "",
                                colorTexto = colorTextoPrincipal,
                                onVerTodo = onVerTodoSeccion,
                                onRestauranteClick = { rest -> onRestauranteClick(rest) }
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                        }
                    }
                }
            }
        }

        when {
            estadoConfig is EstadoCarga.Cargando || (filas.isEmpty() && restaurantes.isEmpty()) -> {
                item(key = "shimmer_banner") {
                    com.yendo.mx.ui.components.BannerShimmerSkeleton(isDark = isDark)
                }
                item(key = "shimmer_chips") {
                    com.yendo.mx.ui.components.CategoriasChipsShimmerSkeleton(isDark = isDark)
                }
                items(count = 3, key = { "shimmer_rest_$it" }, contentType = { "shimmer" }) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 6.dp),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        com.yendo.mx.ui.components.RestauranteCardShimmerSkeleton(isDark = isDark, modifier = Modifier.weight(1f))
                        com.yendo.mx.ui.components.RestauranteCardShimmerSkeleton(isDark = isDark, modifier = Modifier.weight(1f))
                    }
                }
            }
            idZonaActual == null || estadoConfig is EstadoCarga.Error -> {
                item {
                    Column(modifier = Modifier.fillMaxWidth().padding(32.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.LocationOff, null, modifier = Modifier.size(60.dp), tint = colorTextoSecundario)
                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Fuera de Cobertura", fontWeight = FontWeight.Black, fontSize = 20.sp, color = colorTextoPrincipal)
                        Text("Actualmente no tenemos restaurantes disponibles.", textAlign = TextAlign.Center, color = colorTextoSecundario, modifier = Modifier.padding(top = 8.dp, bottom = 24.dp))
                    }
                }
            }
            filas.isEmpty() -> {
                item { Box(modifier = Modifier.fillMaxWidth().height(150.dp), contentAlignment = Alignment.Center) { Text("No hay negocios disponibles aquí 😅", color = colorTextoSecundario, fontWeight = FontWeight.Bold) } }
            }
            else -> {
                item(key = "titulo_seccion") {
                    Text(if (categoriaSeleccionada == "Todos") "Todos" else categoriaSeleccionada, fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal, modifier = Modifier.padding(horizontal = 16.dp))
                    Spacer(modifier = Modifier.height(16.dp))
                }

                items(
                    items = filas,
                    key = { fila -> fila.idKey },
                    contentType = { "restaurantes_row" }
                ) { fila ->
                    val configZonaVal = ConfigManager.configActiva
                    val deliveryZonaOk = (configZonaVal?.logistica?.delivery_activo ?: true) && com.yendo.mx.utils.LogisticaUtils.estaPlataformaAbierta(configZonaVal)
                    Row(
                        modifier = Modifier.animateItem().fillMaxWidth().padding(horizontal = 16.dp), 
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        fila.restaurantes.forEach { rest ->
                            val onClickCard = remember(rest.id) { { onRestauranteClick(rest) } }
                            CardRestauranteCuadrado(
                                rest = rest, 
                                modifier = Modifier.weight(1f), 
                                deliveryGlobalZonaActivo = deliveryZonaOk,
                                onClick = onClickCard
                            )
                        }
                        if (fila.restaurantes.size == 1) Spacer(modifier = Modifier.weight(1f))
                    }
                    Spacer(modifier = Modifier.height(16.dp))
                }
            }
        }
    }
}

@Composable
fun CardRestauranteCuadrado(
    rest: RestauranteFirebase, 
    modifier: Modifier, 
    tiempoActualMs: Long = remember { getAhora() },
    deliveryGlobalZonaActivo: Boolean = true,
    onClick: () -> Unit
) {
    val operacionActiva = remember(rest.id, rest.operacion, tiempoActualMs) {
        val pausaHasta = rest.operacion.pausaHasta
        val estaEnPanico = rest.operacion.pausadoManualmente && pausaHasta > tiempoActualMs
        val estaAbiertoNormal = validarRestauranteAbierto(rest)
        estaAbiertoNormal && !estaEnPanico
    }

    val permiteEnvio = remember(rest.id, rest.operacion.deliveryActivo, rest.operacion.deliveryPropioActivo, deliveryGlobalZonaActivo, operacionActiva) {
        operacionActiva && rest.operacion.deliveryActivo && (deliveryGlobalZonaActivo || rest.operacion.tieneFlotaPropiaActiva)
    }

    val permiteRecoger = remember(rest.id, rest.operacion.pickupActivo, operacionActiva) {
        operacionActiva && rest.operacion.pickupActivo
    }

    val isDark = isSystemInDarkTheme()
    val colorImgBg = if (isDark) Color(0xFF2C2C2E) else Color(0xFFEFEFEF)
    val colorTexto = if (isDark) Color.White else Color.Black

    val context = LocalPlatformContext.current
    val imageRequest: Any? = remember(rest.infoPublica.logotipo, context) {
        if (rest.infoPublica.logotipo.isNotEmpty()) {
            if (com.yendo.mx.utils.esAndroid()) {
                ImageRequest.Builder(context)
                    .data(rest.infoPublica.logotipo)
                    .size(350, 350)
                    .memoryCachePolicy(coil3.request.CachePolicy.ENABLED)
                    .diskCachePolicy(coil3.request.CachePolicy.ENABLED)
                    .crossfade(false)
                    .build()
            } else {
                rest.infoPublica.logotipo
            }
        } else null
    }

    Column(modifier = modifier.clip(RoundedCornerShape(16.dp)).clickable { onClick() }) {
        Box(modifier = Modifier.fillMaxWidth().aspectRatio(1f).clip(RoundedCornerShape(16.dp)).background(colorImgBg)) {
            if (imageRequest != null) {
                AsyncImage(model = imageRequest, contentDescription = null, modifier = Modifier.fillMaxSize(), contentScale = ContentScale.Crop)
            }
            Surface(modifier = Modifier.align(Alignment.TopEnd).padding(8.dp), shape = RoundedCornerShape(8.dp), color = Color.Transparent) {
                RestauranteRatingBadge(reputacion = rest.reputacion)
            }
            
            // 🚀 INYECCIÓN: Botón de Favorito en Comida
            Box(modifier = Modifier.align(Alignment.TopStart).padding(8.dp)) {
                com.yendo.mx.ui.components.BotonFavorito(
                    id = rest.id,
                    tipo = "RESTAURANTE",
                    size = 32.dp,
                    iconSize = 18.dp
                )
            }
            
            if (!operacionActiva) {
                Box(
                    modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.55f)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = "CERRADO",
                        color = Color.White,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Black,
                        letterSpacing = 1.sp,
                        textAlign = TextAlign.Center
                    )
                }
            }
        }

        Column(modifier = Modifier.padding(top = 8.dp, start = 4.dp, end = 4.dp, bottom = 12.dp)) {
            // 1. BADGES DUALES DE ENVÍO Y RECOGER (ARRIBA DEL NOMBRE)
            Row(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (permiteEnvio) {
                    Surface(
                        color = Color(0xFF2196F3).copy(alpha = 0.12f),
                        shape = RoundedCornerShape(6.dp),
                        border = BorderStroke(0.5.dp, Color(0xFF2196F3).copy(alpha = 0.4f))
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                imageVector = Icons.Default.TwoWheeler,
                                contentDescription = "Envío",
                                tint = Color(0xFF2196F3),
                                modifier = Modifier.size(11.dp)
                            )
                            Spacer(Modifier.width(3.dp))
                            Text(
                                text = "Envío",
                                fontSize = 10.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF2196F3)
                            )
                        }
                    }
                }

                if (permiteRecoger) {
                    Surface(
                        color = Color(0xFFFF9800).copy(alpha = 0.12f),
                        shape = RoundedCornerShape(6.dp),
                        border = BorderStroke(0.5.dp, Color(0xFFFF9800).copy(alpha = 0.4f))
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                imageVector = Icons.Default.Storefront,
                                contentDescription = "Recoger",
                                tint = Color(0xFFFF9800),
                                modifier = Modifier.size(11.dp)
                            )
                            Spacer(Modifier.width(3.dp))
                            Text(
                                text = "Recoger",
                                fontSize = 10.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFFFF9800)
                            )
                        }
                    }
                }
            }

            // 2. NOMBRE DEL NEGOCIO (ABAJO DE LOS CHIPS)
            Text(
                text = rest.infoPublica.nombre.ifEmpty { "Negocio" },
                fontWeight = FontWeight.ExtraBold,
                fontSize = 14.sp,
                minLines = 2,
                maxLines = 2,
                lineHeight = 18.sp,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.padding(top = 4.dp),
                color = colorTexto
            )
        }
    }
}

@Composable
fun CategoriasHorizontal(listaChips: List<ChipFiltro>, categoriaActual: String, onCategoriaSeleccionada: (String) -> Unit) {
    val isDark = isSystemInDarkTheme()
    val colorBgInactivo = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF0F0F0)
    val colorTextoInactivo = if (isDark) Color.LightGray else Color.DarkGray

    LazyRow(contentPadding = PaddingValues(horizontal = 16.dp), horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.padding(bottom = 8.dp)) {
        items(
            items = listaChips,
            key = { it.nombre },
            contentType = { "chip" }
        ) { chip ->
            val seleccionado = categoriaActual == chip.nombre
            val bgColor = if (seleccionado) RosaYendo else colorBgInactivo
            val textColor = if (seleccionado) Color.White else colorTextoInactivo

            Box(modifier = Modifier.clip(RoundedCornerShape(24.dp)).background(bgColor).clickable { onCategoriaSeleccionada(chip.nombre) }.padding(horizontal = 16.dp, vertical = 10.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (chip.emoji.isNotEmpty()) {
                        Text(text = chip.emoji, fontSize = 14.sp)
                        Spacer(modifier = Modifier.width(6.dp))
                    }
                    Text(text = chip.nombre, color = textColor, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                }
            }
        }
    }
}
